import { useAuth } from "@/src/contexts/auth.context";
import { Season, Transaction } from "@/src/models/rewardly.model";
import {
	useDeleteStarTransaction,
	useGetAllSeasons,
	useGetStarTransactionsInfinite,
} from "@/src/services/api/rewardly/rewardly.hooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import * as XLSX from 'xlsx';
import { formatReward } from "./use-my-stars";

export const RECOGNITIONS_PAGE_SIZE = 10;

const useRecognitions = () => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const { orgId } = useAuth();
	const { data: seasonsData } = useGetAllSeasons(orgId as string);

	const seasons: Season[] = seasonsData?.getseasons?.items ?? [];

	// autoselect latest season
	const latestSeason = seasons[0]; // assumes sorted desc from API
	const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
	const resolvedSeasonId = selectedSeasonId ?? latestSeason?.ItemId ?? null;
	const selectedSeason = seasons.find(s => s.ItemId === resolvedSeasonId);
	const isCurrentSeason = selectedSeason?.status === "active";

	const seasonOptions = seasons.map(s => ({
		label: s.name,
		value: s.ItemId,
	}));

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useGetStarTransactionsInfinite(resolvedSeasonId);

	// Load every page so search covers all recognitions in the season, not just
	// the first loaded page.
	useEffect(() => {
		if (hasNextPage && !isFetchingNextPage) fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const { mutateAsync: deleteTransaction, isPending: isDeleting } = useDeleteStarTransaction();

	const allTransactions = useMemo(() => (
		(data?.pages.flatMap((page: any) => page?.getstarTransactions?.items ?? []) ?? []) as Transaction[]
	).map(transaction => ({
		...transaction,
		rewardValue: formatReward(transaction.amount ?? 0, selectedSeason?.monetaryValue),
	})), [data, selectedSeason?.monetaryValue]);

	// Search and paginate the loaded transactions client-side (the query has
	// no server-side search; 10/page per the reference design).
	const filteredTransactions = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return allTransactions;
		return allTransactions.filter(transaction =>
			[
				transaction.senderInfo?.displayName,
				transaction.senderInfo?.email,
				transaction.recipientInfo?.displayName,
				transaction.recipientInfo?.email,
			].some(field => (field ?? "").toLowerCase().includes(query)),
		);
	}, [allTransactions, search]);

	const totalItems = filteredTransactions.length;
	const totalPages = Math.max(1, Math.ceil(totalItems / RECOGNITIONS_PAGE_SIZE));
	const currentPage = Math.min(page, totalPages);
	const transactions = filteredTransactions.slice(
		(currentPage - 1) * RECOGNITIONS_PAGE_SIZE,
		currentPage * RECOGNITIONS_PAGE_SIZE,
	);

	// The infinite query loads 20 at a time; keep pulling pages in the
	// background while the user is near the end of what's loaded.
	const maybeLoadMore = () => {
		if (hasNextPage && !isFetchingNextPage) fetchNextPage();
	};

	const handleSearch = (text: string) => {
		setSearch(text);
		setPage(1);
	};

	const handleSeasonChange = (value: string) => {
		setSelectedSeasonId(value);
		setPage(1);
	};

	const handlePageChange = (nextPage: number) => {
		setPage(nextPage);
		if (nextPage >= totalPages) maybeLoadMore();
	};

	const handleDelete = async (transactionId: string) => {
		try {
			await deleteTransaction(transactionId);
			Toast.show({ type: "success", text1: t("MSG_RECOGNITION_DELETED") });
		} catch {
			Toast.show({ type: "error", text1: t("ERROR_RECOGNITION_DELETE_FAILED") });
		}
	};

	const [isExporting, setIsExporting] = useState(false);
	const handleExport = () => {
		setIsExporting(true);
		try {
			const rows = filteredTransactions.map(transaction => ({
				"Sender": transaction.senderInfo?.displayName ?? "",
				"Sender Email": transaction.senderInfo?.email ?? "",
				"Recipient": transaction.recipientInfo?.displayName ?? "",
				"Recipient Email": transaction.recipientInfo?.email ?? "",
				"Stars Sent": transaction.amount ?? 0,
				"Reward": transaction.rewardValue,
				"Reason": transaction.message ?? "",
				"Date Sent": new Date(transaction.CreatedDate).toLocaleDateString(),
			}));

			const ws = XLSX.utils.json_to_sheet(rows);
			ws['!cols'] = [
				{ wch: 25 }, { wch: 30 }, { wch: 25 }, { wch: 30 },
				{ wch: 12 }, { wch: 12 }, { wch: 40 }, { wch: 14 },
			];

			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "Recognitions");
			XLSX.writeFile(wb, `season-${seasonOptions.find(s => s.value === resolvedSeasonId)?.label ?? resolvedSeasonId}-recognitions.xlsx`);
		} catch {
			Toast.show({ type: "error", text1: t("ERROR_EXPORT_FAILED") });
		} finally {
			setIsExporting(false);
		}
	};

	return {
		search, handleSearch,
		selectedSeasonId: resolvedSeasonId,
		handleSeasonChange,
		seasonOptions,
		isCurrentSeason,
		transactions,
		totalItems,
		totalPages,
		currentPage,
		handlePageChange,
		isLoading,
		handleDelete,
		isDeleting,
		handleExport,
		isExporting,
	};
};

export default useRecognitions;
